import { parseCompositeKey } from "../../../flatbuffers/types/ln2MessageOptions";

/**
 * Defensive check for the bank editor: a channel's heartbeat message must be a
 * message the paired adapter actually maps, otherwise the heartbeat never
 * arrives and the channel is flagged offline at runtime.
 *
 * This can only be evaluated once a channel and an adapter are associated on a
 * bank — at channel-creation time there is no adapter relationship to filter
 * the heartbeat message against. The comparison is at message-category
 * granularity because the adapter's mapped message set is enumerated per
 * category (see BankOffsetMatrix.extractMappedMessageTypes).
 *
 * Returns the unmapped heartbeat category key when a warning is warranted, or
 * null when there is nothing to warn about:
 *   - no heartbeat configured on the channel,
 *   - no adapter selected,
 *   - the adapter's messages are not enumerable (empty category set — opaque
 *     protocols such as MCE/Diligent), so we cannot prove the heartbeat is
 *     unmapped and must not raise a false positive,
 *   - the heartbeat category is present in the adapter's mapped set.
 */
export default function unmappedHeartbeatCategory(
  heartbeatKey: string | null | undefined,
  adapterCategoryKeys: readonly string[],
): string | null {
  if (!heartbeatKey) return null;
  if (adapterCategoryKeys.length === 0) return null;
  const parsed = parseCompositeKey(heartbeatKey);
  if (!parsed) return null;
  if (adapterCategoryKeys.includes(parsed.categoryKey)) return null;
  return parsed.categoryKey;
}
